/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react'

// Modal configuration interface
export interface ModalConfig {
  id: string
  component: ReactNode
  onClose?: () => void
  keepParentModalsOpen?: boolean
}

// Modal controller interface
export interface ModalController {
  showModal: (componentOrString: ReactNode | string, options?: { keepParentModalsOpen?: boolean }) => string
  hideModal: (id?: string) => void
  hideAllModals: () => void
  openedModals: Record<string, ModalConfig>
}

// Create the modal context
const ModalContext = createContext<ModalController | undefined>(undefined)

// Custom hook to use modal context
export const useModal = (): ModalController => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}

// Modal Provider Component
export function ModalProvider({ children }: { readonly children: ReactNode }) {
  const [openedModals, setOpenedModals] = useState<Record<string, ModalConfig>>({})
  const modalCounterRef = useRef(0)

  // Show a new modal
  const showModal = useCallback(
    (componentOrString: ReactNode | string, options?: { keepParentModalsOpen?: boolean }): string => {
      // If it's a string, just log it (legacy support)
      if (typeof componentOrString === 'string') {
        console.log('Modal requested (string):', componentOrString)
        return '' // Return empty string for string-based calls
      }

      const id = `modal-${Date.now()}-${modalCounterRef.current++}`

      const modalConfig: ModalConfig = {
        id,
        component: componentOrString,
        onClose: undefined,
        keepParentModalsOpen: options?.keepParentModalsOpen
      }

      setOpenedModals((prev) => {
        // If keepParentModalsOpen is false (default), clear previous modals
        const baseModals = options?.keepParentModalsOpen ? prev : {}
        return {
          ...baseModals,
          [id]: modalConfig
        }
      })

      return id
    },
    []
  )

  // Hide a specific modal or all modals
  const hideModal = useCallback((id?: string) => {
    if (!id) {
      // Clear all modals
      setOpenedModals({})
      return
    }

    setOpenedModals((prev) => {
      const newModals = { ...prev }
      delete newModals[id]
      return newModals
    })
  }, [])

  // Hide all modals
  const hideAllModals = useCallback(() => {
    setOpenedModals({})
  }, [])

  const controller: ModalController = {
    showModal,
    hideModal,
    hideAllModals,
    openedModals
  }

  return <ModalContext.Provider value={controller}>{children}</ModalContext.Provider>
}

export default ModalContext
